import { useState } from 'react';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function UplistingSyncAuditPanel() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const runAudit = async () => {
    setLoading(true);
    setResults(null);
    try {
      const { data } = await diagnosticsAPI.syncAudit();
      setResults(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const coverageGap = results?.coverage_gap ?? [];
  const orphans = results?.orphan_property_ids_in_reservations ?? [];
  const syncErrors = results?.recent_sync_errors ?? [];
  const latestSync = results?.latest_sync_log;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Uplisting Sync Audit</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Button onClick={runAudit} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Run Sync Audit
          </Button>
          {latestSync && (
            <span className="text-xs text-muted-foreground">
              Last sync: {latestSync.timestamp || latestSync.synced_at || 'unknown'}
              {latestSync.properties_synced != null && ` (${latestSync.properties_synced} properties)`}
            </span>
          )}
        </div>

        {results && (
          <div className="space-y-4">
            {/* Coverage Gap Table */}
            {coverageGap.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Coverage Gap ({coverageGap.filter((g) => g.gap !== 'ok').length} properties missing/partial)
                </p>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">Property Name</th>
                        <th className="text-left px-2 py-1.5 font-medium">Uplisting ID</th>
                        <th className="text-right px-2 py-1.5 font-medium">2026 Reservations</th>
                        <th className="text-right px-2 py-1.5 font-medium">Total</th>
                        <th className="text-left px-2 py-1.5 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coverageGap.map((prop, i) => (
                        <tr
                          key={prop.uplisting_id || i}
                          className={`border-b last:border-0 ${
                            prop.gap === 'missing'
                              ? 'bg-red-50/50 dark:bg-red-950/30'
                              : prop.gap === 'partial'
                                ? 'bg-amber-50/50 dark:bg-amber-950/30'
                                : ''
                          }`}
                        >
                          <td className="px-2 py-1">{prop.name || '-'}</td>
                          <td className="px-2 py-1 font-mono text-[10px]">{prop.uplisting_id}</td>
                          <td className={`px-2 py-1 text-right tabular-nums ${
                            prop.gap === 'missing' ? 'text-red-600 dark:text-red-400 font-medium' :
                            prop.gap === 'partial' ? 'text-amber-600 dark:text-amber-400 font-medium' : ''
                          }`}>
                            {prop.reservations_2026 ?? 0}
                          </td>
                          <td className="px-2 py-1 text-right tabular-nums">{prop.total_reservations ?? 0}</td>
                          <td className="px-2 py-1">
                            <Badge
                              variant={prop.gap === 'missing' ? 'destructive' : prop.gap === 'partial' ? 'outline' : 'secondary'}
                              className="text-[10px]"
                            >
                              {prop.gap}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                All portfolio properties have reservations in the database.
              </div>
            )}

            {/* Recent Sync Errors */}
            {syncErrors.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  Recent Sync Errors ({syncErrors.length})
                </p>
                <div className="overflow-x-auto border border-red-200 dark:border-red-800 rounded-lg">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-red-50 dark:bg-red-950">
                        <th className="text-left px-2 py-1.5 font-medium">Timestamp</th>
                        <th className="text-left px-2 py-1.5 font-medium">Failed Properties</th>
                      </tr>
                    </thead>
                    <tbody>
                      {syncErrors.map((err, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-2 py-1 tabular-nums">{err.timestamp || err.synced_at || '-'}</td>
                          <td className="px-2 py-1">
                            <div className="flex flex-wrap gap-1">
                              {(err.failed_properties || []).map((p, j) => (
                                <Badge key={j} variant="destructive" className="text-[10px]">
                                  {p}
                                </Badge>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Orphan Property IDs */}
            {orphans.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
                  Orphan property IDs in reservations ({orphans.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">Property ID</th>
                        <th className="text-left px-2 py-1.5 font-medium">Sample Name</th>
                        <th className="text-right px-2 py-1.5 font-medium">Total</th>
                        <th className="text-right px-2 py-1.5 font-medium">2026</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orphans.map((o, i) => (
                        <tr key={o.property_id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono text-[10px]">{o.property_id}</td>
                          <td className="px-2 py-1">{o.sample_property_name || '-'}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{o.total_reservations ?? '-'}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{o.reservations_2026 ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            {/* Raw counts summary */}
            {results.reservation_counts_by_property && results.reservation_counts_by_property.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
                  All reservation counts ({results.reservation_counts_by_property.length} property IDs in DB)
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0">
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">Property ID</th>
                        <th className="text-left px-2 py-1.5 font-medium">Name</th>
                        <th className="text-right px-2 py-1.5 font-medium">2026</th>
                        <th className="text-right px-2 py-1.5 font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...results.reservation_counts_by_property]
                        .sort((a, b) => (a.reservations_2026 ?? 0) - (b.reservations_2026 ?? 0))
                        .map((r) => (
                          <tr key={r.property_id} className={`border-b last:border-0 ${r.reservations_2026 === 0 ? 'bg-red-50/50 dark:bg-red-950/30' : ''}`}>
                            <td className="px-2 py-1 font-mono text-[10px]">{r.property_id}</td>
                            <td className="px-2 py-1">{r.sample_property_name || '-'}</td>
                            <td className={`px-2 py-1 text-right tabular-nums ${r.reservations_2026 === 0 ? 'text-red-600 dark:text-red-400 font-medium' : ''}`}>
                              {r.reservations_2026 ?? 0}
                            </td>
                            <td className="px-2 py-1 text-right tabular-nums">{r.total_reservations ?? 0}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
